import { useState } from 'react';
import dayjs from 'dayjs';
import {
  DaySequenceChangeInput,
  DaySequenceStatus,
} from '@core/generated/gql-types';
import { TimesheetApprovalItem } from '@core/timesheetApproval/timesheetApproval.types';
import { minutesToMilliseconds } from '@core/util/datetime.util';
import { formatTimeForTimePicker } from '@core/util/jobTimezone.util';
import { actionLinkClassName } from '@core/theme/actionTheme.util';
import Button from '@/components/Button';
import { buildTimesheetChange } from '@/modules/timesheets/utils/timesheetGroups.utils';
import {
  formatBreakTime,
  formatLoggedTime,
  formatNetLoggedTime,
  formatTimeRangeAndDuration,
  getBreakTimeInMinutes,
} from '@/utils/utils';

interface TimesheetCardProps {
  item: TimesheetApprovalItem;
  canEdit: boolean;
  showClientName: boolean;
  isEditing: boolean;
  isSaving: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (change: DaySequenceChangeInput) => Promise<void>;
}

const fieldClassName =
  'w-28 rounded-md border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-[var(--action-color)] focus:ring-1 focus:ring-[var(--action-color)] focus:outline-none';

const TimesheetCardEditForm = ({
  item,
  isSaving,
  onCancelEdit,
  onSave,
}: Omit<
  TimesheetCardProps,
  'canEdit' | 'showClientName' | 'isEditing' | 'onStartEdit'
>) => {
  const { daySequence } = item;
  const hasProposedChange =
    daySequence.status === DaySequenceStatus.ProposedChange;
  const clockInMs =
    (hasProposedChange
      ? daySequence.proposedClockInTime
      : daySequence.clockInTime) || null;
  const clockOutMs =
    (hasProposedChange
      ? daySequence.proposedClockOutTime
      : daySequence.clockOutTime) || null;
  const breaks = hasProposedChange
    ? daySequence.proposedBreaks || []
    : daySequence.breaks || [];

  const [startTime, setStartTime] = useState(() =>
    formatTimeForTimePicker(clockInMs, null, item.jobTimezone, ''),
  );
  const [endTime, setEndTime] = useState(() =>
    formatTimeForTimePicker(clockOutMs, null, item.jobTimezone, ''),
  );
  const [breakTimeMinutes, setBreakTimeMinutes] = useState(() =>
    String(Math.round(getBreakTimeInMinutes(breaks))),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const breakMinutes = parseInt(breakTimeMinutes, 10) || 0;
  const previewChange = buildTimesheetChange(
    item,
    startTime,
    endTime,
    breakMinutes,
  );
  const durationLabel = previewChange
    ? formatLoggedTime(
        previewChange.clockInTimestamp,
        previewChange.clockOutTimestamp,
        minutesToMilliseconds(breakMinutes),
      )
    : null;
  const isBreakTooLong = Boolean(previewChange) && durationLabel === '0 hrs';
  const canSave = Boolean(previewChange) && !isBreakTooLong && !isSaving;

  const handleSave = async () => {
    const change = buildTimesheetChange(item, startTime, endTime, breakMinutes);
    if (!change || isBreakTooLong) {
      setErrorMessage(
        isBreakTooLong
          ? 'Break time must be shorter than the shift.'
          : 'End time must be after the start time.',
      );
      return;
    }
    setErrorMessage(null);
    await onSave(change);
  };

  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-gray-500">Start/end time</span>
        <div className="flex gap-2">
          <input
            type="time"
            aria-label="Start time"
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
            className={fieldClassName}
          />
          <input
            type="time"
            aria-label="End time"
            value={endTime}
            onChange={(event) => setEndTime(event.target.value)}
            className={fieldClassName}
          />
        </div>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-gray-500">Break</span>
        <input
          type="number"
          min="0"
          step="1"
          aria-label="Break"
          value={breakTimeMinutes}
          placeholder="mins"
          onChange={(event) => {
            const value = event.target.value;
            if (value === '') {
              setBreakTimeMinutes('');
              return;
            }
            const minutes = parseInt(value, 10);
            if (!Number.isNaN(minutes) && minutes >= 0) {
              setBreakTimeMinutes(value);
            }
          }}
          onBlur={() => {
            if (breakTimeMinutes === '' || Number.isNaN(breakMinutes)) {
              setBreakTimeMinutes('0');
            }
          }}
          className={fieldClassName}
        />
      </div>
      {(errorMessage || isBreakTooLong) && (
        <p role="alert" className="text-sm text-red-600">
          {errorMessage || 'Break time must be shorter than the shift.'}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={isSaving}
          onClick={onCancelEdit}
          className="!w-auto !rounded-lg !px-4 !py-2 !text-sm !font-medium"
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={!canSave}
          isLoading={isSaving}
          onClick={() => {
            void handleSave();
          }}
          className="!w-auto !rounded-lg !px-4 !py-2 !text-sm !font-medium"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

const TimesheetCard = ({
  item,
  canEdit,
  showClientName,
  isEditing,
  isSaving,
  onStartEdit,
  onCancelEdit,
  onSave,
}: TimesheetCardProps) => {
  const { daySequence } = item;
  const hasProposedChange =
    daySequence.status === DaySequenceStatus.ProposedChange;
  const clockIn = hasProposedChange
    ? daySequence.proposedClockInTime
    : daySequence.clockInTime;
  const clockOut = hasProposedChange
    ? daySequence.proposedClockOutTime
    : daySequence.clockOutTime;
  const breaks = hasProposedChange
    ? daySequence.proposedBreaks || []
    : daySequence.breaks || [];

  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="max-w-2/3 min-w-0">
          <p className="text-sm font-medium whitespace-nowrap text-gray-900">
            {daySequence.startDateISO
              ? dayjs(daySequence.startDateISO).format('ddd, D MMM')
              : '—'}
          </p>
          <p className="text-sm text-gray-500">
            {[showClientName ? item.clientName : null, item.siteName]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
          {hasProposedChange && (
            <p className={`text-xs font-medium ${actionLinkClassName}`}>
              Proposed change
            </p>
          )}
        </div>
        {canEdit && !isEditing && (
          <button
            type="button"
            onClick={onStartEdit}
            className={`shrink-0 cursor-pointer text-sm font-medium ${actionLinkClassName}`}
          >
            Edit Timesheet
          </button>
        )}
      </div>
      <p className="mt-3 text-sm font-medium text-gray-900">
        {item.jobTitle || '—'}
      </p>
      {isEditing ? (
        <TimesheetCardEditForm
          item={item}
          isSaving={isSaving}
          onCancelEdit={onCancelEdit}
          onSave={onSave}
        />
      ) : (
        <div className="mt-3 space-y-1 text-sm">
          <div className="flex justify-between gap-3">
            <span className="text-gray-500">Start/end time</span>
            <span className="text-gray-900">
              {formatTimeRangeAndDuration(clockIn, clockOut, item.jobTimezone, {
                includeDuration: false,
              })}
            </span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-gray-500">Break</span>
            <span className="text-gray-900">{formatBreakTime(breaks)}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-gray-500">Logged time</span>
            <span className="text-gray-900">
              {formatNetLoggedTime(clockIn, clockOut, breaks)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetCard;
